import { Dispatch, PropsWithChildren, SetStateAction, useLayoutEffect, useState } from "react";
import { IonHeader, IonInput, IonItem, IonLabel } from "@ionic/react";
import { App, AuthLayout, Button, Colors, fontLarge, fontMedium, Icon, PasswordField } from "main";
import { RouteComponentProps } from "react-router";

import { translate } from "~components/string-table";

type SubmissibleArgs = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

type SignUpProps = {
  app: App;
  history: RouteComponentProps["history"];
  location: RouteComponentProps["location"];
  loginPath: string;
  onSignUp: (args: SubmissibleArgs) => void;
};

export function SignUp(props: SignUpProps) {
  const { app, history, location, loginPath, onSignUp } = props;
  const locationState = location.state as { email: "value" } | undefined;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const containsUpperCase = /[A-Z]/.test(password);
  const containsLowerCase = /[a-z]/.test(password);
  const containsNumber = /\d/.test(password);
  const isLongEnough = password.length >= 8;

  const isButtonEnabled =
    firstName &&
    lastName &&
    containsUpperCase &&
    containsLowerCase &&
    containsNumber &&
    isLongEnough &&
    password === confirmPassword;

  useLayoutEffect(() => {
    if (!locationState || !locationState.email) {
      return history.push("/");
    }
  }, [history, locationState]);

  const onSubmit = ({ firstName, lastName, email, password }: SubmissibleArgs) => {
    onSignUp({ firstName, lastName, email, password });
  };

  // This is just to satisfy typescript. This will
  // never be hit since the `useLayoutEffect` call
  // will redirect to login.
  if (!locationState?.email) return;

  const email = locationState.email;

  return (
    <AuthLayout
      app={app}
      title={translate("SIGN_UP_PAGE_HEADER_LABEL")}
      onBackNav={() => {
        history.push({
          pathname: loginPath,
          state: {
            email,
          },
        });
      }}
    >
      <p css={{ ...fontLarge({ fontWeight: 600 }) }}>{translate("SIGN_UP_HEADER_LABEL")}</p>
      <div css={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div css={{ paddingLeft: 16 }}>
          <p css={{ ...fontLarge({ fontWeight: 600 }) }}>
            {translate("EMAIL_FIELD_LOGIN_INPUT_PLACEHOLDER")}
          </p>
          <p>{locationState?.email}</p>
        </div>

        <FormInputItem
          label={translate("SIGN_UP_FIRST_NAME_LABEL")}
          value={firstName}
          setValue={setFirstName}
        />

        <FormInputItem
          label={translate("SIGN_UP_LAST_NAME_LABEL")}
          value={lastName}
          setValue={setLastName}
        />

        <PasswordField
          password={password}
          onPasswordInput={setPassword}
          passwordLabel={translate("SIGN_UP_PASSWORD_LABEL")}
        />

        <PasswordField
          password={confirmPassword}
          onPasswordInput={setConfirmPassword}
          passwordLabel={translate("SIGN_UP_CONFIRM_PASSWORD_LABEL")}
        />

        <div
          css={{
            background: "var(--swing-slate200)",
            margin: "16px 0",
            padding: "8px 4px 12px 12px",
            borderRadius: 6,
          }}
        >
          <p css={{ margin: "4px 0" }}>All passwords must have:</p>
          <RequirementItem isValid={containsUpperCase}>1 uppercase character</RequirementItem>
          <RequirementItem isValid={containsLowerCase}>1 lowercase character</RequirementItem>
          <RequirementItem isValid={containsNumber}>1 number</RequirementItem>
          <RequirementItem isValid={isLongEnough}>8 characters minimum</RequirementItem>
        </div>
        <Button
          type="secondary"
          onClick={() => {
            console.log("woot kwan");
            onSubmit({ firstName, lastName, email, password });
          }}
          isFluid
          status={isButtonEnabled ? "enabled" : "disabled"}
        >
          {translate("SIGN_UP_BUTTON_LABEL")}
        </Button>
      </div>
    </AuthLayout>
  );
}

/**** Helper Components ****/

function RequirementItem(props: PropsWithChildren<{ isValid: boolean }>) {
  const { children, isValid } = props;
  return (
    <div css={{ display: "flex", gap: 4, opacity: isValid ? 1 : 0.3 }}>
      <Icon name={isValid ? "Check" : "Close"}></Icon>
      {children}
    </div>
  );
}

type FormInputItemProps = {
  value: string;
  label: string;
  setValue: Dispatch<SetStateAction<string>>;
  isPasswordField?: boolean;
};

function FormInputItem(props: PropsWithChildren<FormInputItemProps>) {
  const { value, setValue, label } = props;
  return (
    <div>
      <IonHeader className="ion-padding-start ion-no-border">
        <IonLabel position="stacked" css={{ ...fontMedium({ fontWeight: 500 }) }}>
          {label}
        </IonLabel>
      </IonHeader>
      <IonItem
        lines="full"
        css={{
          width: "100%",
          "--ion-item-background": "var(--ion-color-light)",
          background: "var(--ion-color-light)",
          "--border-color": Colors.slate400,
          "--min-height": "auto",
        }}
      >
        <IonInput
          type="text"
          value={value}
          css={{ flex: 1 }}
          id="fname"
          onIonInput={(e: CustomEvent) => {
            setValue(e.detail.value);
          }}
          spellCheck="false"
          autocomplete="one-time-code"
        />
      </IonItem>
    </div>
  );
}
